import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';
import { Loader2 } from 'lucide-react';

const CreateTenantDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    tenant_name: '',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_password: 'ChangeMe123!',
    subscription_tier: 'starter'
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.tenant_name || !formData.admin_email || !formData.admin_first_name || !formData.admin_last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      // Create tenant with admin user in one API call
      const response = await api.post('/tenants/create-with-admin', formData);
      
      toast.success(`Tenant "${formData.tenant_name}" created successfully!`);
      
      // Reset form
      setFormData({
        tenant_name: '',
        admin_email: '',
        admin_first_name: '',
        admin_last_name: '',
        admin_password: 'ChangeMe123!',
        subscription_tier: 'starter'
      });
      
      onSuccess(response.data);
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Tenant</DialogTitle>
          <DialogDescription>
            Set up a new Consignor tenant with their admin user account
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tenant Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">Tenant Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Tenant Name *</Label>
                <Input
                  value={formData.tenant_name}
                  onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                  placeholder="e.g., Bluegrass Consignment"
                />
              </div>

              <div className="space-y-2">
                <Label>Subscription Tier</Label>
                <Select value={formData.subscription_tier} onValueChange={(value) => setFormData({ ...formData, subscription_tier: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Admin User Info */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-semibold text-sm">Admin User</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.admin_first_name}
                  onChange={(e) => setFormData({ ...formData, admin_first_name: e.target.value })}
                  placeholder="John"
                />
              </div>

              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.admin_last_name}
                  onChange={(e) => setFormData({ ...formData, admin_last_name: e.target.value })}
                  placeholder="Doe"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  placeholder="john@bluegrassconsignment.com"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Temporary Password</Label>
                <Input
                  value={formData.admin_password}
                  onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                  placeholder="Temporary password"
                />
                <p className="text-xs text-muted-foreground">Admin will be prompted to change on first login</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={creating}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {creating ? 'Creating...' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTenantDialog;
